import { useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Bars } from 'react-loader-spinner';
import { Button, ProgressBar, Spinner } from 'react-bootstrap';
import Badge from '../../components/base/Badge';
import AdvanceTable from '../../components/base/AdvanceTable';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import AdvanceTableFooter from '../../components/base/AdvanceTableFooter';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUp,
  faArrowDown,
  faPen,
  faRightLeft,
  faScissors,
  faFlagCheckered,
  faShuffle
} from '@fortawesome/free-solid-svg-icons';
import { useMachineQueue } from '../../features/jobs/queues/hooks/useQueues.js';
import MachineExecutionHistoryPanel from '../../components/machines/MachineExecutionHistoryPanel.js';
import MachineConsumablesSimulatorPanel from '../../components/machines/MachineConsumablesSimulatorPanel.js';
import MoveToProductModal from '../../components/machines/MoveToProductModal.js';
// The two repairs a planner reaches for while standing at this screen. They
// existed only on the read-only queue page, so which machines appeared to have
// them came down to which machines had a row to show — on everything but the
// busiest line they looked like an L120 feature. Same modals, one definition.
import DrumSurgeryModal from '../../components/machines/DrumSurgeryModal.js';
import ForceCompleteModal from '../../components/machines/ForceCompleteModal.js';
import ForecastPanel from '../../components/machines/ForecastPanel.js';
import { queueRowsToForecastTokens } from '../../features/machine-intervals/api/machineIntervalsApi.js';
// The queue's three levels of joining-up — machine order, merge-execution
// blocks, group-by runs — used to live in this file, which is why the plain
// queue list and the Factory-3D panel showed a group-by launch as loose
// executions. One copy now, in helpers/machineQueueBlocks.js.
import {
  buildBlockRows,
  flattenGroups,
  getJobColor,
  getPrimaryOutput,
  getPrimaryTokenId
} from '../../helpers/machineQueueBlocks.js';
import { redisFetch } from '../../features/jobs/api/http';

// The operator panel now opens INSIDE the app.
//
// It used to be `window.open('http://<host>:5005/<machine>-hmi')`, which is a
// private address on a port that is not on the tunnel: from outside the factory
// the tab opened on nothing, and over HTTPS the browser blocked it as mixed
// content before trying. The page itself is unchanged — see helpers/hmiEmbed.js.
const hmiPanelPath = machineId =>
  `/apps/machine-management/machine-hmi/${String(machineId || '').toLowerCase()}`;

const MachineQueueEdit = () => {
  const { machineId } = useParams();
  const mid = String(machineId || '').toLowerCase();

  const { data, isLoading, error, refetch } = useMachineQueue(mid);
  const groups = data?.groups || [];
  const queuePath = `/apps/machine-management/machine-queue/${mid}`;

  const [view, setView] = useState('queue'); // 'queue' | 'history' | 'simulate'

  // Queue in the order it will actually run — each drum separately, because the
  // transition between two drums of the same product is exactly what gets costed.
  const forecastTokens = useMemo(
    () => queueRowsToForecastTokens(flattenGroups(groups)),
    [groups]
  );

  if (isLoading) {
    return (
      <Bars
        height="80"
        width="80"
        radius="9"
        color="#3874ff"
        ariaLabel="loading"
        wrapperStyle={styles.spinnerContainer}
      />
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <h4>Error</h4>
        <pre className="text-danger">{String(error?.message || error)}</pre>
      </div>
    );
  }

  return (
    <div className="mb-9 mt-0">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-3">
        <div>
          <h2 className="mb-1">Machine Queue Editor</h2>
          <div className="text-700">
            Machine: <b>{data?.machineId}</b> • Queue size:{' '}
            <b>{data?.queueSize ?? 0}</b>
          </div>
        </div>

        {/* Four buttons with long labels do not fit across a phone. The row had
            no flex-wrap, so instead of breaking they pushed past the viewport
            and the page grew a horizontal scrollbar. Below sm they become a
            2×2 grid; from sm up it is the same wrapping row as before.
            gridTemplateColumns is inert once display is flex, so the inline
            style does not need a breakpoint of its own. */}
        <div
          className="d-grid d-sm-flex flex-wrap align-items-center gap-2"
          style={{ gridTemplateColumns: '1fr 1fr' }}
        >
          <Button
            variant={view === 'history' ? 'secondary' : 'outline-primary'}
            onClick={() => setView(v => (v === 'history' ? 'queue' : 'history'))}
          >
            {view === 'history' ? 'Hide History' : 'Execution History'}
          </Button>
          <Button
            variant={view === 'simulate' ? 'success' : 'outline-success'}
            onClick={() => setView(v => (v === 'simulate' ? 'queue' : 'simulate'))}
          >
            {view === 'simulate' ? 'Hide Simulate' : 'Simulate Consumables'}
          </Button>
          <Button
            as={Link}
            to={hmiPanelPath(mid)}
            variant="outline-info"
            title="The operator panel, inside the app — works from outside the factory too"
          >
            Show HMI Operator
          </Button>
          <Button as={Link} to={queuePath} variant="primary">
            Back to Queue
          </Button>
        </div>
      </div>

      <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
        {view === 'history' ? (
          <MachineExecutionHistoryPanel machineId={mid} />
        ) : view === 'simulate' ? (
          <MachineConsumablesSimulatorPanel machineId={mid} groups={groups} />
        ) : (
          <>
            {/* How long this queue will actually take: production plus the
                measured cost of every colour/product/drum change in it. */}
            <ForecastPanel
              className="mt-3"
              machineId={mid}
              tokens={forecastTokens}
            />
            <MachineQueueEditTable
              machineId={mid}
              rows={groups}
              onRefresh={refetch}
            />
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  spinnerContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  }
};

export default MachineQueueEdit;

/* =========================
   Helpers
========================= */

function fmt2(n) {
  const x = Number(n);
  return Number.isFinite(x) ? x.toFixed(2) : '0.00';
}

function safeNum(n) {
  const x = Number(n);
  return Number.isFinite(x) ? x : 0;
}

function upper(s) {
  return String(s || '').toUpperCase();
}

function phaseLabel(p) {
  if (p === 'unwinding') return 'UNWINDING';
  if (p === 'pre_stranding') return 'PRE-STRANDING';
  if (p === 'conductor_isolation') return 'ISOLATION';
  if (p === 'rope') return 'ROPE';
  if (p === 'interior_sheat') return 'INNER SHEATH';
  if (p === 'armouring') return 'ARMOURING';
  if (p === 'exterior_sheat') return 'OUTER SHEATH';
  if (p === 'coiling') return 'COILING';
  if (p === 'rewinding') return 'REWINDING';
  return upper(p);
}

function colorHex(color) {
  const c = upper(color);
  if (c === 'GREEN-YELLOW') return '#9acd32';
  if (c === 'BLUE') return '#2f7cff';
  if (c === 'BROWN') return '#8b5a2b';
  if (c === 'BLACK') return '#111111';
  if (c === 'GREY' || c === 'GRAY') return '#7a7a7a';
  if (c === 'WHITE') return '#e5e5e5';
  if (c === 'RED') return '#d9534f';
  if (c === 'YELLOW') return '#f0c400';
  if (c === 'GREEN') return '#2e8b57';
  if (c === 'ORANGE') return '#f39c12';
  return '#8892a0';
}

function statusBg(status) {
  const s = upper(status);
  if (s === 'ACTIVE' || s === 'RUNNING') return 'success';
  if (s === 'DONE' || s === 'COMPLETED' || s === 'FINISHED') return 'primary';
  if (s === 'PARTIAL') return 'warning';
  if (s === 'FAILED' || s === 'BLOCKED' || s === 'CONSUMED') return 'danger';
  return 'warning';
}

function StatusBadge({ status }) {
  const s = upper(status || 'PENDING');
  return (
    <Badge variant="phoenix" bg={statusBg(s)}>
      {s}
    </Badge>
  );
}

/** The three spellings the backend uses for a drum that is finished. */
const isDoneStatus = status =>
  ['DONE', 'COMPLETED', 'FINISHED'].includes(upper(status || ''));

function buildTokenAvailability(tok) {
  const status = upper(tok?.status || '');
  const available = safeNum(tok?.availableLength);
  const produced = safeNum(tok?.producedLength);

  if (status === 'ACTIVE' || status === 'RUNNING') return 'IN LUCRU';
  if (status === 'COMPLETED' || status === 'DONE' || status === 'FINISHED')
    return 'DISPONIBIL';
  if (produced > 0 && available > 0) return 'PARTIAL';
  if (produced > 0 && available <= 0) return 'CONSUMAT';
  if (status === 'PENDING') return 'NELIVRAT';

  return 'BLOCAT';
}

function availabilityBg(label) {
  const s = upper(label);
  if (s === 'DISPONIBIL') return 'success';
  if (s === 'IN LUCRU') return 'info';
  if (s === 'PARTIAL') return 'warning';
  return 'danger';
}

function formatCopperLine(item) {
  const wireType = upper(item?.wireType || '');
  const nWires = item?.numberOfWires;
  const nDrums = item?.numberOfDrums;
  const d = item?.wireDiameter;

  if (wireType === 'SINGLE-WIRE') {
    return `Cu Ø ${fmt2(d)} mm`;
  }

  if (wireType === 'STRANDED') {
    if (nDrums && nWires && d) {
      return `${nDrums} x ${nWires} x ${fmt2(d)} mm`;
    }
    if (nWires && d) {
      return `${nWires} x ${fmt2(d)} mm`;
    }
    return 'Cupru stranded';
  }

  if (wireType === 'MULTI-WIRE') {
    if (nWires && d) {
      return `${nWires} x ${fmt2(d)} mm`;
    }
    return 'Cupru multi-wire';
  }

  return 'Cupru';
}

function materialPlanDirectLines(task) {
  const lines = [];
  const mp = task?.materialPlan || [];

  for (const item of mp) {
    if (!item) continue;

    const mat = upper(item.material || '');

    if (mat === 'COPPER') {
      lines.push(formatCopperLine(item));
    } else {
      let text = item.material || '';
      if (item.grade) {
        if (text) text += ` ${item.grade}`;
        else text = item.grade;
      }
      if (!text && item.layer) text = item.layer;
      if (text) lines.push(text);
    }
  }

  return lines;
}

function mergeUniqueInputs(block) {
  const seen = {};
  const result = [];

  for (const item of block?.items || []) {
    const task = item?.task || {};
    const inputs = task.inputs || [];

    for (const tok of inputs) {
      const key = String(tok?.tokenId || Math.random());
      if (!seen[key]) {
        seen[key] = true;
        result.push(tok);
      }
    }
  }

  return result;
}

function mergeMaterialPlanDirectLines(block) {
  const seen = {};
  const lines = [];

  for (const item of block?.items || []) {
    const taskLines = materialPlanDirectLines(item?.task || {});
    for (const line of taskLines) {
      if (!seen[line]) {
        seen[line] = true;
        lines.push(line);
      }
    }
  }

  return lines;
}

function getExecutionText(task, tok) {
  const phase = String(task?.phase || '');
  const deliveryMode = String(task?.deliveryMode || '').toLowerCase();

  if (phase === 'coiling') {
    const count = safeNum(task?.coilCount);
    const len = safeNum(task?.coilLength);
    if (count > 0 && len > 0) {
      return `${count} x ${fmt2(len)} m`;
    }
  }

  if (phase === 'rewinding') {
    const count = safeNum(task?.rewindCount);
    const len = safeNum(task?.rewindLength);
    if (count > 0 && len > 0) {
      return `${count} x ${fmt2(len)} m`;
    }
  }

  if (phase === 'exterior_sheat' && deliveryMode === 'coils_on_machine') {
    const count = safeNum(task?.coilCount);
    const len = safeNum(task?.coilLength);

    if (count > 0 && len > 0) {
      return `${count} x ${fmt2(len)} m`;
    }

    if (tok) {
      return `1 x ${fmt2(safeNum(tok?.plannedLength))} m`;
    }

    return `1 x ${fmt2(safeNum(task?.plannedLength))} m`;
  }

  if (tok) {
    return `1 x ${fmt2(safeNum(tok?.plannedLength))} m`;
  }

  return `1 x ${fmt2(safeNum(task?.plannedLength))} m`;
}

function getDrumText(task, outputTok) {
  const material =
    upper(outputTok?.drum_material || task?.drum_material || 'METAL') ||
    'METAL';
  const diameter = outputTok?.drum_diameter || task?.drum_diameter || null;

  return diameter ? `${material} • Ø ${fmt2(diameter)}` : material;
}

/* =========================
   Job column
========================= */

function JobCell({ products = [], machineId, selectedJobs, onToggleJob, isGrouped = false }) {
  const navigate = useNavigate();
  if (!products.length) return <span className="text-700">—</span>;

  const openEdit = p => {
    if (!p?.jobId || !p?.phase) return;
    // Edit this product's phase (all its drums) in the launched-job wizard, then
    // return to this machine queue. Reuses ProjectDetails' edit-phase-wizard entry.
    navigate('/apps/project-management/edit-phase-wizard', {
      state: {
        jobId: p.jobId,
        phase: p.phase,
        returnTo: `/apps/machine-management/machine-queue/${machineId}/edit`
      }
    });
  };

  return (
    <div className="d-flex flex-column gap-2 align-items-center justify-content-center text-center w-100">
      {products.map(p => {
        const jobColor = getJobColor(p.jobId);

        return (
          <div
            key={p.key}
            className="d-flex align-items-center justify-content-center gap-2 w-100"
          >
            {onToggleJob && p.jobId && (
              <input
                type="checkbox"
                checked={!!selectedJobs?.has(p.jobId)}
                onChange={() => onToggleJob(p.jobId)}
                title={
                  isGrouped
                    ? 'Merged / grouped — selecting this selects the whole group (they must be edited together)'
                    : 'Select to edit / merge with others'
                }
                style={{ flexShrink: 0, cursor: 'pointer' }}
              />
            )}
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: jobColor,
                display: 'inline-block',
                flexShrink: 0
              }}
            />
            <div className="text-center">
              <div className="fw-medium text-900">{p.productLabel}</div>
              <div className="fs-10 text-700">
                LOT: {fmt2(p.plannedLength)} m
              </div>
              {p.greenYellowSection ? (
                <div className="fs-10 fw-semibold text-warning">
                  GY (verde-galben): {p.greenYellowSection} mm²
                </div>
              ) : null}
            </div>
            {/* Hide the per-product Edit for merged / group-by products (editing one
                alone would break the group → use "Edit selected together"), and also
                while a selection is active. */}
            {p.jobId && p.phase && !isGrouped && !(selectedJobs?.size >= 1) && (
              <Button
                size="sm"
                variant="phoenix-secondary"
                className="px-2 py-1"
                title={`Edit ${phaseLabel(p.phase)} for this product`}
                onClick={() => openEdit(p)}
              >
                <FontAwesomeIcon icon={faPen} className="fs-10" />
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* =========================
   Queue block renderer
========================= */

function QueueBlockCard({
  block,
  machineId,
  onRefresh,
  isFirst,
  isLast,
  onMoveBlock,
  rowId,
  selection,
  isSelected,
  onToggleBlock,
  onSurgery,
  onForceComplete
}) {
  const hasSelection = (selection?.count || 0) > 0;
  // The first selected row is the one that keeps its arrows — see `selection`
  // in MachineQueueEditTable for why the others give theirs up.
  const isAnchor = hasSelection && selection.anchorId === rowId;
  const upDisabled = hasSelection
    ? !selection.canUp || !selection.contiguous
    : isFirst;
  const downDisabled = hasSelection
    ? !selection.canDown || !selection.contiguous
    : isLast;
  const [busyDirection, setBusyDirection] = useState(null);

  const firstTask = block?.items?.[0]?.task || {};
  const firstOutput = getPrimaryOutput(firstTask);
  const mergedInputs = mergeUniqueInputs(block);
  const mergedLines = mergeMaterialPlanDirectLines(block);

  const isShared = (block?.items?.length || 0) > 1;

  const totalPlanned = (block?.items || []).reduce(
    (acc, item) => acc + safeNum(item?.task?.plannedLength),
    0
  );
  const totalExecuted = (block?.items || []).reduce(
    (acc, item) => acc + safeNum(item?.task?.executedLength),
    0
  );
  const progress = totalPlanned
    ? Math.max(
        0,
        Math.min(100, Math.round((totalExecuted / totalPlanned) * 100))
      )
    : 0;

  const executionText = isShared
    ? (block.items || [])
        .map(item => `1 x ${fmt2(safeNum(item?.task?.plannedLength))} m`)
        .join(' + ')
    : getExecutionText(firstTask, firstOutput);

  const displayedStatus = upper(
    firstOutput?.status || firstTask?.status || 'PENDING'
  );
  const drumText = getDrumText(firstTask, firstOutput);
  const dotColor = colorHex(firstOutput?.color || firstTask?.color);

  async function move(direction) {
    // With a selection active, move all of it. Without one, move this block —
    // the WHOLE block, every entry in it. Sending only the first task is what
    // broke merge-executions: the server moved one half of the pair and left
    // the other behind, and their adjacency IS the merge.
    const entries = selection?.count
      ? selection.entries
      : (block?.items || []).map(item => ({
          jobId: item.group?.jobId,
          taskId: item.task?.taskId,
          tokenId: getPrimaryTokenId(item.task)
        }));

    if (!entries.length) return;

    try {
      setBusyDirection(direction);

      const res = await redisFetch(
        `/api/redis/queues/${machineId}/move`,
        {
          method: 'POST',
          body: JSON.stringify({ entries, direction })
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.error(`move ${direction} failed`, res.status, data);
        return;
      }

      onRefresh?.();
    } catch (err) {
      console.error(`move ${direction} error`, err);
    } finally {
      setBusyDirection(null);
    }
  }

  return {
    nrDate: (
      <div className="text-center">
        <div className="fw-semibold">
          {safeNum(block?.items?.[0]?._queueSeq) + 1}
        </div>
        <div className="fs-10 text-700">
          {new Date().toLocaleDateString('ro-RO')}
        </div>
      </div>
    ),

    faza: (
      <div className="text-center">
        <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
          {firstTask.tandem && Array.isArray(firstTask.tandemPhases) && firstTask.tandemPhases.length ? (
            <span className="fw-medium text-900 d-flex flex-column">
              {firstTask.tandemPhases.map(p => (
                <span key={p}>{phaseLabel(p)}</span>
              ))}
            </span>
          ) : (
            <span className="fw-medium text-900">
              {phaseLabel(firstTask.phase)}
            </span>
          )}
          {dotColor ? (
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                border: '1px solid rgba(255,255,255,0.15)',
                background: dotColor,
                display: 'inline-block'
              }}
            />
          ) : null}
        </div>
        <div className="fs-10 text-600 mt-1">
          {isShared ? `${block.items.length} task-uri` : firstTask.taskId}
        </div>
      </div>
    ),

    input: (
      <div className="d-flex flex-column gap-1">
        {mergedInputs.length ? (
          mergedInputs.map((tok, i) => {
            const label = buildTokenAvailability(tok);
            return (
              <div key={tok?.tokenId || i} className="fs-10">
                <Badge variant="phoenix" bg={availabilityBg(label)}>
                  {tok?.tokenId || '-'}
                </Badge>{' '}
                <span className="text-700">
                  {fmt2(safeNum(tok?.availableLength))} m
                </span>
              </div>
            );
          })
        ) : mergedLines.length ? (
          mergedLines.map((line, i) => (
            <div key={i} className="fs-10 text-800 fw-normal">
              {line}
            </div>
          ))
        ) : (
          <span className="text-700 fs-10">-</span>
        )}
      </div>
    ),

    inExecutie: (
      <div className="text-center">
        <div className="font-monospace fs-10 text-800 fw-normal">
          {executionText}
        </div>
      </div>
    ),

    executata: (
      <div>
        <div className="text-center font-monospace fs-10 text-800 fw-normal">
          {fmt2(totalExecuted)}/{fmt2(totalPlanned)} m
        </div>
        <ProgressBar
          now={progress}
          style={{ height: 6 }}
          className="mt-2 bg-body-tertiary"
        />
      </div>
    ),

    tambur: (
      <div className="text-center">
        <div className="fs-10 text-800 fw-normal">{drumText}</div>
      </div>
    ),

    status: (
      <div className="d-flex justify-content-center">
        <StatusBadge status={displayedStatus} />
      </div>
    ),

    select: (
      <div className="d-flex justify-content-center">
        <input
          type="checkbox"
          checked={!!isSelected}
          onChange={() => onToggleBlock?.(rowId)}
          title={
            (block?.items?.length || 0) > 1
              ? 'Merged drum — it is one pass of the machine, so it always moves as one'
              : 'Select to move several drums together'
          }
        />
      </div>
    ),

    actiuni: hasSelection && !isAnchor ? (
      // Deliberately blank. Arrows here would read as "move this row", which is
      // not what they would do while a selection is active.
      <div className="text-center text-700 fs-10" title="Moving with the selection above">
        ↕ with selection
      </div>
    ) : (
      // Five buttons do not fit a column this narrow in one line, and a row that
      // overflows pushes the table into a horizontal scroll.
      <div className="d-flex align-items-center justify-content-center flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline-primary"
          disabled={upDisabled || !!busyDirection}
          title={
            hasSelection
              ? selection.contiguous
                ? `Move the ${selection.count} selected drums up`
                : 'The selected drums are not next to each other'
              : 'Move block up'
          }
          className="d-flex align-items-center justify-content-center"
          style={{ width: 34, height: 34, padding: 0 }}
          onClick={() => move('up')}
        >
          {busyDirection === 'up' ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <FontAwesomeIcon icon={faArrowUp} />
          )}
        </Button>

        <Button
          size="sm"
          variant="outline-primary"
          disabled={downDisabled || !!busyDirection}
          title={
            hasSelection
              ? selection.contiguous
                ? `Move the ${selection.count} selected drums down`
                : 'The selected drums are not next to each other'
              : 'Move block down'
          }
          className="d-flex align-items-center justify-content-center"
          style={{ width: 34, height: 34, padding: 0 }}
          onClick={() => move('down')}
        >
          {busyDirection === 'down' ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <FontAwesomeIcon icon={faArrowDown} />
          )}
        </Button>

        {/* Re-assigning to another product is a per-block action; with a
            selection active it would be ambiguous, so it steps aside. So are the
            two repairs below it. */}
        {!hasSelection && (
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={!!busyDirection}
            title="Move to another product"
            className="d-flex align-items-center justify-content-center"
            style={{ width: 34, height: 34, padding: 0 }}
            onClick={() => onMoveBlock?.(block)}
          >
            <FontAwesomeIcon icon={faRightLeft} />
          </Button>
        )}

        {/* The drums of this run, and the four repairs — split one that is
            really two, merge, cancel, move. On every machine: the queue is the
            screen that disagrees with the floor first, whichever line it is. */}
        {!hasSelection && (
          <Button
            size="sm"
            variant="outline-info"
            disabled={!!busyDirection}
            title="Drums of this run — split one that is really two, merge, cancel or move"
            className="d-flex align-items-center justify-content-center"
            style={{ width: 34, height: 34, padding: 0 }}
            onClick={() =>
              onSurgery?.({
                jobId: block?.items?.[0]?.group?.jobId,
                taskId: firstTask.taskId
              })
            }
          >
            <FontAwesomeIcon icon={faScissors} />
          </Button>
        )}

        {/* On the drum that is RUNNING, and on any that has not started.
            Running: the drum end the line never registered — the pinoles opened,
            the bobbin came off and no sensor said so. Pending: a drum that was
            made and never tracked, or one the planner is closing out at the
            length that actually exists; it has no counter and no operators, so
            its length has to be stated, and the modal switches on the status it
            is given. A merged block is ONE physical drum, so its shared token is
            the one to close; the server refuses anything that is not what it
            says it is. */}
        {!hasSelection && !isDoneStatus(displayedStatus) && (
          <Button
            size="sm"
            variant="outline-success"
            disabled={!!busyDirection}
            title={
              displayedStatus === 'ACTIVE'
                ? 'Finish this drum now, at the length it really holds, and let the queue move on'
                : 'Close this drum without running it, at a length you state'
            }
            className="d-flex align-items-center justify-content-center"
            style={{ width: 34, height: 34, padding: 0 }}
            onClick={() =>
              onForceComplete?.({
                machineId,
                jobId: block?.items?.[0]?.group?.jobId,
                productLabel:
                  block?.items?.[0]?.group?.productLabel ||
                  block?.items?.[0]?.group?.name ||
                  null,
                taskId: firstTask.taskId,
                tokenId: getPrimaryTokenId(firstTask),
                status: displayedStatus,
                produced: safeNum(firstOutput?.producedLength),
                planned: safeNum(firstOutput?.plannedLength ?? firstTask.plannedLength)
              })
            }
          >
            <FontAwesomeIcon icon={faFlagCheckered} />
          </Button>
        )}
      </div>
    )
  };
}

/* =========================
   Table
========================= */

const MachineQueueEditTable = ({ rows = [], machineId, onRefresh }) => {
  const displayRows = useMemo(() => buildBlockRows(rows), [rows]);
  const navigate = useNavigate();

  // Products in the same MERGE (shared drum) or GROUP-BY run must be edited
  // TOGETHER — never a partial group. Map each grouped jobId → the full set of
  // jobIds it must be edited with (union of the grouped rows' product sets).
  const jobGroupMap = useMemo(() => {
    const parent = {};
    const find = x => {
      while (parent[x] !== x) {
        parent[x] = parent[parent[x]];
        x = parent[x];
      }
      return x;
    };
    for (const r of displayRows) {
      if (!r.isGrouped) continue;
      const ids = (r.products || []).map(p => String(p.jobId)).filter(Boolean);
      ids.forEach(id => {
        if (!(id in parent)) parent[id] = id;
      });
      for (let i = 1; i < ids.length; i++) parent[find(ids[i])] = find(ids[0]);
    }
    const groups = {};
    for (const id of Object.keys(parent)) {
      const root = find(id);
      (groups[root] = groups[root] || []).push(id);
    }
    const map = {};
    for (const members of Object.values(groups)) {
      for (const id of members) map[id] = members;
    }
    return map;
  }, [displayRows]);

  // Multi-select: pick several products to open (and optionally merge) together.
  const [selectedJobs, setSelectedJobs] = useState(() => new Set());
  // Block being re-assigned to another product (Move to… modal).
  const [moveBlock, setMoveBlock] = useState(null);
  // The run whose drums are being repaired, and the running drum somebody has
  // asked to close by hand. Held here, not per row, so only one is ever open.
  const [surgery, setSurgery] = useState(null);
  const [forceTarget, setForceTarget] = useState(null);

  // ── reordering several drums at once ──────────────────────────────────────
  //
  // Separate from `selectedJobs`, which picks PRODUCTS to edit. This picks
  // DRUMS to move, and the two questions have different answers: a merge pair
  // is two products but one drum.
  const [selectedBlocks, setSelectedBlocks] = useState(() => new Set());

  const toggleBlock = id =>
    setSelectedBlocks(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selection = useMemo(() => {
    // displayRows is already in machine order, so this is too
    const picked = displayRows.filter(r => selectedBlocks.has(r.id));
    const indexes = picked.map(r => r.blockIndex);

    return {
      count: picked.length,
      // Only the FIRST selected row keeps its buttons. With a selection active,
      // arrows on the other rows would look like they move that row alone —
      // they would not, and finding out by pressing one is not a good way to
      // learn it.
      anchorId: picked.length ? picked[0].id : null,
      entries: picked.flatMap(r =>
        (r.block?.items || []).map(item => ({
          jobId: item.group?.jobId,
          taskId: item.task?.taskId,
          tokenId: getPrimaryTokenId(item.task)
        }))
      ),
      canUp: indexes.length ? Math.min(...indexes) > 0 : true,
      canDown: indexes.length
        ? Math.max(...indexes) < displayRows.length - 1
        : true,
      // A gapped selection has no honest answer for what happens to the drum in
      // the middle, so the server refuses it — say so here rather than let the
      // planner press a button that 400s.
      contiguous:
        indexes.length <= 1 ||
        indexes.every((b, k) => k === 0 || b === indexes[k - 1] + 1)
    };
  }, [displayRows, selectedBlocks]);
  // Toggling a grouped product toggles its WHOLE merge/group-by set, so you can
  // never enter edit with only some members of a group selected.
  const toggleJob = jobId => {
    const members = jobGroupMap[String(jobId)] || [String(jobId)];
    setSelectedJobs(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) members.forEach(m => next.delete(m));
      else members.forEach(m => next.add(m));
      return next;
    });
  };

  // ── TRANSFORM / FORK, FROM THE QUEUE ─────────────────────────────────────
  //
  // Asked for 2026-10-30: *"nu-mi apare butonul de transform/Fork, decat in
  // project token state … /machine-queue/:machineId/edit"*. The planner is
  // already looking at the drums here, and going back to Project Token State to
  // tick the same three rows is a detour.
  //
  // The hand-over is the SAME shape Project Token State makes — `sourceJobId`
  // plus `rows` of `{ tokenId }` — so the wizard needs no new entry path and
  // its `bring all products and phases` toggle still reveals the rest of the
  // lot. Every output of the selected product's queue entries goes in, not just
  // the primary one: a drum split by the scissors leaves one task holding two.
  const forkSelection = useMemo(() => {
    const jobIds = [...selectedJobs].map(String);
    if (jobIds.length !== 1) {
      return {
        ok: false,
        why:
          jobIds.length > 1
            ? 'A fork has ONE source product. Clear the others first.'
            : 'Tick a product to fork.'
      };
    }
    const jobId = jobIds[0];

    const rows = [];
    const seen = new Set();
    const phases = new Set();
    for (const r of displayRows) {
      for (const item of r.block?.items || []) {
        if (String(item?.group?.jobId) !== jobId) continue;
        const phase = item?.task?.phase ? String(item.task.phase) : '';
        if (phase) phases.add(phase);
        for (const out of item?.task?.outputs || []) {
          const tokenId = out?.tokenId ? String(out.tokenId) : '';
          if (!tokenId || seen.has(tokenId)) continue;
          seen.add(tokenId);
          rows.push({
            jobId,
            tokenId,
            phase,
            color: out?.color ?? null,
            productLabel: item?.group?.productLabel || item?.group?.name || ''
          });
        }
      }
    }

    if (!rows.length) {
      return { ok: false, why: 'That product has no drum queued on this machine.' };
    }
    // The same gate Project Token State applies: one source, one branch phase.
    // Two phases of one job on one machine (l75 runs isolation AND rope) would
    // move the same metres twice — a later phase consumes the earlier one's
    // drums — so the wizard locks to one phase and so does this.
    if (phases.size > 1) {
      return {
        ok: false,
        jobId,
        rows,
        why:
          `This product has drums at ${phases.size} phases on this machine ` +
          `(${[...phases].map(phaseLabel).join(', ')}). A fork branches at ONE ` +
          `phase — open it from Project Token State and tick that phase's drums.`
      };
    }

    return { ok: true, jobId, rows, phase: [...phases][0] || '', why: '' };
  }, [selectedJobs, displayRows]);

  const forkSelected = () => {
    if (!forkSelection.ok) return;
    navigate('/apps/project-management/transform-wizard', {
      state: {
        sourceJobId: forkSelection.jobId,
        rows: forkSelection.rows,
        // Back to this queue, not to Project Monitor: the planner came from
        // here and the next thing they do is look at what the fork did to it.
        returnTo: `/apps/machine-management/machine-queue/${machineId}/edit`
      }
    });
  };

  // Open the Reconcile wizard with the selected products (whole merge/group-by
  // sets) — reconcile executions across ALL their phases (swap/re-attribute).
  const reconcileSelected = () => {
    const full = new Set([...selectedJobs].map(String));
    for (const id of [...full]) (jobGroupMap[id] || []).forEach(m => full.add(m));
    const jobIds = [...full];
    if (jobIds.length < 1) return;
    navigate('/apps/project-management/reconcile-wizard', {
      state: {
        jobIds,
        machineId,
        returnTo: `/apps/machine-management/machine-queue/${machineId}/edit`
      }
    });
  };

  const editSelectedTogether = () => {
    // pull in every group member of anything selected → never edit a partial
    // merge / group-by set (toggleJob already enforces this; belt-and-suspenders).
    const full = new Set([...selectedJobs].map(String));
    for (const id of [...full]) (jobGroupMap[id] || []).forEach(m => full.add(m));
    const jobIds = [...full];
    if (jobIds.length < 1) return;
    // all products on this machine are at the same (machine's) phase
    const phase = displayRows
      .flatMap(r => r.products || [])
      .find(p => full.has(String(p.jobId)))?.phase;

    // Detect group-by mode from the selected products' queue entries so the edit
    // wizard pre-enables the toggle → editing preserves the color/Ø grouping unless
    // deselected. Key shape: GB::<phase>::<kind>::<value> (kind = color | wd).
    const groupBy = {};
    for (const r of displayRows) {
      if (!(r.products || []).some(p => full.has(String(p.jobId)))) continue;
      const key = r.block?.items?.[0]?.task?.groupByKey;
      if (!key) continue;
      const parts = String(key).split('::');
      if (parts.length < 4) continue;
      groupBy[parts[1]] = parts[2] === 'wd' ? 'wireDiameter' : 'color';
    }

    navigate('/apps/project-management/edit-phase-wizard', {
      state: {
        jobIds,
        phase,
        ...(Object.keys(groupBy).length ? { groupBy } : {}),
        returnTo: `/apps/machine-management/machine-queue/${machineId}/edit`
      }
    });
  };

  const columns = [
    {
      accessorKey: 'select',
      header: '',
      cell: ({ row: { original } }) => {
        const card = QueueBlockCard({
          block: original.block,
          machineId,
          onRefresh,
          isFirst: original.blockIndex === 0,
          isLast: original.blockIndex === displayRows.length - 1,
          onMoveBlock: setMoveBlock,
          rowId: original.id,
          selection,
          isSelected: selectedBlocks.has(original.id),
          onToggleBlock: toggleBlock
        });
        return card.select;
      },
      meta: {
        headerProps: { style: { width: '3%', textAlign: 'center' } },
        cellProps: () => ({
          style: { verticalAlign: 'middle', textAlign: 'center' }
        })
      }
    },
    {
      accessorKey: 'nrDate',
      header: 'No. / Date',
      cell: ({ row: { original } }) => {
        const card = QueueBlockCard({
          block: original.block,
          machineId,
          onRefresh,
          isFirst: original.blockIndex === 0,
          isLast: original.blockIndex === displayRows.length - 1,
          onMoveBlock: setMoveBlock,
          rowId: original.id,
          selection,
          isSelected: selectedBlocks.has(original.id),
          onToggleBlock: toggleBlock
        });
        return card.nrDate;
      },
      meta: {
        headerProps: { style: { width: '7%', textAlign: 'center' } },
        cellProps: ({ row }) => ({
          style: {
            verticalAlign: 'middle',
            textAlign: 'center',
            borderLeft: `4px solid ${row.original.borderColor}`
          }
        })
      }
    },
    {
      accessorKey: 'job',
      header: 'Job',
      cell: ({ row: { original } }) =>
        original.showJob ? (
          <JobCell
            products={original.products}
            machineId={machineId}
            selectedJobs={selectedJobs}
            onToggleJob={toggleJob}
            isGrouped={original.isGrouped}
          />
        ) : (
          <div style={{ minHeight: 24 }} />
        ),
      meta: {
        headerProps: { style: { width: '16%', textAlign: 'center' } },
        cellProps: {
          style: { verticalAlign: 'middle', textAlign: 'center' }
        }
      }
    },
    {
      accessorKey: 'faza',
      header: 'Phase',
      cell: ({ row: { original } }) => {
        const card = QueueBlockCard({
          block: original.block,
          machineId,
          onRefresh,
          isFirst: original.blockIndex === 0,
          isLast: original.blockIndex === displayRows.length - 1,
          onMoveBlock: setMoveBlock,
          rowId: original.id,
          selection,
          isSelected: selectedBlocks.has(original.id),
          onToggleBlock: toggleBlock
        });
        return card.faza;
      },
      meta: {
        headerProps: { style: { width: '12%', textAlign: 'center' } },
        cellProps: {
          style: { verticalAlign: 'middle', textAlign: 'center' }
        }
      }
    },
    {
      accessorKey: 'input',
      header: 'Input',
      cell: ({ row: { original } }) => {
        const card = QueueBlockCard({
          block: original.block,
          machineId,
          onRefresh,
          isFirst: original.blockIndex === 0,
          isLast: original.blockIndex === displayRows.length - 1,
          onMoveBlock: setMoveBlock,
          rowId: original.id,
          selection,
          isSelected: selectedBlocks.has(original.id),
          onToggleBlock: toggleBlock
        });
        return card.input;
      },
      meta: {
        headerProps: { style: { width: '10%' } },
        cellProps: {
          style: { verticalAlign: 'middle' }
        }
      }
    },
    {
      accessorKey: 'inExecutie',
      header: 'Quantity in progress',
      cell: ({ row: { original } }) => {
        const card = QueueBlockCard({
          block: original.block,
          machineId,
          onRefresh,
          isFirst: original.blockIndex === 0,
          isLast: original.blockIndex === displayRows.length - 1,
          onMoveBlock: setMoveBlock,
          rowId: original.id,
          selection,
          isSelected: selectedBlocks.has(original.id),
          onToggleBlock: toggleBlock
        });
        return card.inExecutie;
      },
      meta: {
        headerProps: { style: { width: '12%', textAlign: 'center' } },
        cellProps: {
          style: { verticalAlign: 'middle', textAlign: 'center' }
        }
      }
    },
    {
      accessorKey: 'executata',
      header: 'Quantity executed',
      cell: ({ row: { original } }) => {
        const card = QueueBlockCard({
          block: original.block,
          machineId,
          onRefresh,
          isFirst: original.blockIndex === 0,
          isLast: original.blockIndex === displayRows.length - 1,
          onMoveBlock: setMoveBlock,
          rowId: original.id,
          selection,
          isSelected: selectedBlocks.has(original.id),
          onToggleBlock: toggleBlock
        });
        return card.executata;
      },
      meta: {
        headerProps: { style: { width: '14%', textAlign: 'center' } },
        cellProps: {
          style: { verticalAlign: 'middle', textAlign: 'center' }
        }
      }
    },
    {
      accessorKey: 'tambur',
      header: 'Drum type',
      cell: ({ row: { original } }) => {
        const card = QueueBlockCard({
          block: original.block,
          machineId,
          onRefresh,
          isFirst: original.blockIndex === 0,
          isLast: original.blockIndex === displayRows.length - 1,
          onMoveBlock: setMoveBlock,
          rowId: original.id,
          selection,
          isSelected: selectedBlocks.has(original.id),
          onToggleBlock: toggleBlock
        });
        return card.tambur;
      },
      meta: {
        headerProps: { style: { width: '10%', textAlign: 'center' } },
        cellProps: {
          style: { verticalAlign: 'middle', textAlign: 'center' }
        }
      }
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row: { original } }) => {
        const card = QueueBlockCard({
          block: original.block,
          machineId,
          onRefresh,
          isFirst: original.blockIndex === 0,
          isLast: original.blockIndex === displayRows.length - 1,
          onMoveBlock: setMoveBlock,
          rowId: original.id,
          selection,
          isSelected: selectedBlocks.has(original.id),
          onToggleBlock: toggleBlock
        });
        return card.status;
      },
      meta: {
        headerProps: { style: { width: '10%', textAlign: 'center' } },
        cellProps: {
          style: { verticalAlign: 'middle', textAlign: 'center' }
        }
      }
    },
    {
      accessorKey: 'actiuni',
      header: 'Actions',
      cell: ({ row: { original } }) => {
        const card = QueueBlockCard({
          block: original.block,
          machineId,
          onRefresh,
          isFirst: original.blockIndex === 0,
          isLast: original.blockIndex === displayRows.length - 1,
          onMoveBlock: setMoveBlock,
          rowId: original.id,
          selection,
          isSelected: selectedBlocks.has(original.id),
          onToggleBlock: toggleBlock,
          onSurgery: setSurgery,
          onForceComplete: setForceTarget
        });
        return card.actiuni;
      },
      meta: {
        headerProps: { style: { width: '11%', textAlign: 'center' } },
        cellProps: {
          style: { verticalAlign: 'middle', textAlign: 'center' }
        }
      }
    }
  ];

  const table = useAdvanceTable({
    data: displayRows,
    columns,
    pageSize: 20,
    pagination: true,
    sortable: false
  });

  return (
    <AdvanceTableProvider {...table}>
      {selectedJobs.size > 0 && (
        <div className="d-flex align-items-center gap-2 mb-2">
          <span className="fw-semibold">{selectedJobs.size} selected</span>
          <Button
            size="sm"
            variant="phoenix-primary"
            onClick={editSelectedTogether}
          >
            Edit selected together
            {selectedJobs.size > 1 ? ' (merge)' : ''}
          </Button>
          <Button
            size="sm"
            variant="phoenix-info"
            onClick={reconcileSelected}
          >
            Reconcile
          </Button>
          {/* Fork is a ONE-product verb, so it is shown disabled with the reason
              rather than hidden: a button that appears and disappears as rows
              are ticked is harder to find than one that says why it is off. */}
          <Button
            size="sm"
            variant="phoenix-warning"
            onClick={forkSelected}
            disabled={!forkSelection.ok}
            title={
              forkSelection.ok
                ? `Fork part of this product into another cable, branching at ` +
                  `${phaseLabel(forkSelection.phase)} (${forkSelection.rows.length} drum(s))`
                : forkSelection.why
            }
          >
            <FontAwesomeIcon icon={faShuffle} className="me-1 fs-10" />
            Transform / Fork
          </Button>
          {!forkSelection.ok && selectedJobs.size > 0 && (
            <span className="text-700 fs-10">{forkSelection.why}</span>
          )}
          <Button
            size="sm"
            variant="phoenix-secondary"
            onClick={() => setSelectedJobs(new Set())}
          >
            Clear
          </Button>
        </div>
      )}

      {selection.count > 0 && (
        <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
          <span className="fw-semibold">
            {selection.count} drum{selection.count === 1 ? '' : 's'} picked to
            move
          </span>
          <span className="text-700 fs-9">
            {selection.contiguous
              ? 'the arrows on the first one move them all, in order'
              : 'they are not next to each other — the arrows are off until they are'}
          </span>
          <Button
            size="sm"
            variant="phoenix-secondary"
            onClick={() => setSelectedBlocks(new Set())}
          >
            Clear
          </Button>
        </div>
      )}

      <AdvanceTable
        tableProps={{ className: 'phoenix-table border-top border-200 fs-9' }}
      />
      <AdvanceTableFooter pagination />

      <MoveToProductModal
        show={!!moveBlock}
        block={moveBlock}
        allJobs={rows}
        machineId={machineId}
        onHide={() => setMoveBlock(null)}
        onDone={() => {
          setMoveBlock(null);
          onRefresh?.();
        }}
      />

      <DrumSurgeryModal
        show={!!surgery}
        onHide={() => {
          setSurgery(null);
          onRefresh?.();
        }}
        jobId={surgery?.jobId}
        taskId={surgery?.taskId}
        machineId={machineId}
        // The handover's target is a DRUM on this line, so it needs the queue —
        // the same rows this table is drawn from, `onTheLine` included.
        queueRows={rows}
        onApplied={onRefresh}
      />

      <ForceCompleteModal
        show={!!forceTarget}
        target={forceTarget}
        onHide={() => {
          setForceTarget(null);
          onRefresh?.();
        }}
      />
    </AdvanceTableProvider>
  );
};
